import { forwardRef, Ref, useState, ReactElement, ChangeEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi, API_URL } from 'src/helper';
import axios from 'axios';
import { store } from 'src/store/store';
import {
  Avatar,
  Link,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  lighten,
  List,
  ListItem,
  ListItemAvatar,
  TextField,
  Theme,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Hidden
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import FindInPageTwoToneIcon from '@mui/icons-material/FindInPageTwoTone';

import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import CategoryIcon from '@mui/icons-material/Category';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import PersonIcon from '@mui/icons-material/Person';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};

    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(3)}
`
);

interface SearchResult {
  id: string;
  name: string;
  type: 'category' | 'subcategory' | 'audio' | 'artist' | 'review';
  description?: string;
  route: string;
}

function HeaderSearch() {
  const [openSearchResults, setOpenSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Silent API call function that doesn't show toast errors
  const silentGetApi = async (url: string) => {
    try {
      const token = store.getState().admin.token;
      const response = await axios.get(API_URL + url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      // Silently handle errors without showing toasts
      console.log('Silent API error:', error);
      return null;
    }
  };

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      // Search across different endpoints using silent API calls to avoid toasts
      const [categories, subcategories, audios, artists, reviews] = await Promise.allSettled([
        silentGetApi(`/categories?categoryName=${encodeURIComponent(query)}&page=1&limit=5`),
        silentGetApi(`/subcategories?subCategoryName=${encodeURIComponent(query)}&page=1&limit=5`),
        silentGetApi(`/audios?title=${encodeURIComponent(query)}&page=1&limit=5`),
        silentGetApi(`/artists?name=${encodeURIComponent(query)}&page=1&limit=5`),
        silentGetApi(`/reviews?search=${encodeURIComponent(query)}&page=1&limit=5`)
      ]);

      console.log('Search API responses:', { categories, subcategories, audios, artists, reviews });

      const results: SearchResult[] = [];

      // Process categories
      if (categories.status === 'fulfilled' && categories.value) {
        const categoryData = categories.value as any;
        console.log('Category data:', categoryData);
        const categoryList = categoryData?.categories;
        if (categoryList && Array.isArray(categoryList)) {
          categoryList.forEach((cat: any) => {
            if (cat.categoryName && cat.categoryName.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: cat._id || cat.id,
                name: cat.categoryName,
                type: 'category',
                description: cat.description,
                route: '/dashboard/category'
              });
            }
          });
        }
      } else if (categories.status === 'rejected') {
        console.log('Categories API failed:', categories.reason);
      }

      // Process subcategories
      if (subcategories.status === 'fulfilled' && subcategories.value) {
        const subcategoryData = subcategories.value as any;
        console.log('Subcategory data:', subcategoryData);
        const subcategoryList = subcategoryData?.subCategories;
        if (subcategoryList && Array.isArray(subcategoryList)) {
          subcategoryList.forEach((subcat: any) => {
            if (subcat.subCategoryName && subcat.subCategoryName.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: subcat._id || subcat.id,
                name: subcat.subCategoryName,
                type: 'subcategory',
                description: subcat.description,
                route: '/dashboard/subcategory'
              });
            }
          });
        }
      } else if (subcategories.status === 'rejected') {
        console.log('Subcategories API failed:', subcategories.reason);
      }

      // Process audios
      if (audios.status === 'fulfilled' && audios.value) {
        const audioData = audios.value as any;
        console.log('Audio data:', audioData);
        const audioList = audioData?.audios;
        if (audioList && Array.isArray(audioList)) {
          audioList.forEach((audio: any) => {
            if (audio.title && audio.title.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: audio._id || audio.id,
                name: audio.title,
                type: 'audio',
                description: audio.description,
                route: '/dashboard/audio'
              });
            }
          });
        }
      } else if (audios.status === 'rejected') {
        console.log('Audios API failed:', audios.reason);
      }

      // Process artists
      if (artists.status === 'fulfilled' && artists.value) {
        const artistData = artists.value as any;
        console.log('Artist data:', artistData);
        const artistList = artistData?.artists;
        if (artistList && Array.isArray(artistList)) {
          artistList.forEach((artist: any) => {
            if (artist.name && artist.name.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: artist._id || artist.id,
                name: artist.name,
                type: 'artist',
                description: artist.bio || artist.description,
                route: '/dashboard/artists'
              });
            }
          });
        }
      } else if (artists.status === 'rejected') {
        console.log('Artists API failed:', artists.reason);
      }

      // Process reviews
      if (reviews.status === 'fulfilled' && reviews.value) {
        const reviewData = reviews.value as any;
        console.log('Review data:', reviewData);
        const reviewList = reviewData?.reviews;
        if (reviewList && Array.isArray(reviewList)) {
          reviewList.forEach((review: any) => {
            const reviewTitle = review.title || `Review by ${review.userName || review.user?.name || 'User'}`;
            if (reviewTitle.toLowerCase().includes(query.toLowerCase()) || 
                (review.comment && review.comment.toLowerCase().includes(query.toLowerCase()))) {
              results.push({
                id: review._id || review.id,
                name: reviewTitle,
                type: 'review',
                description: review.comment,
                route: '/dashboard/reviews'
              });
            }
          });
        }
      } else if (reviews.status === 'rejected') {
        console.log('Reviews API failed:', reviews.reason);
      }

      // If no results found from API, add some fallback static results for testing
      if (results.length === 0) {
        // Add static navigation options that match the search query
        const staticOptions = [
          { name: 'Categories', type: 'category', route: '/dashboard/category', description: 'Manage audio categories' },
          { name: 'Subcategories', type: 'subcategory', route: '/dashboard/subcategory', description: 'Manage audio subcategories' },
          { name: 'Audio Files', type: 'audio', route: '/dashboard/audio', description: 'Manage audio files' },
          { name: 'Artists', type: 'artist', route: '/dashboard/artists', description: 'Manage artists' },
          { name: 'Reviews', type: 'review', route: '/dashboard/reviews', description: 'Manage reviews' },
        ];

        staticOptions.forEach((option, index) => {
          if (option.name.toLowerCase().includes(query.toLowerCase()) || 
              option.description.toLowerCase().includes(query.toLowerCase())) {
            results.push({
              id: `static-${index}`,
              name: option.name,
              type: option.type as any,
              description: option.description,
              route: option.route
            });
          }
        });
      }

      setSearchResults(results.slice(0, 10)); // Limit to 10 results
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchValue(value);

    if (value.trim()) {
      if (!openSearchResults) {
        setOpenSearchResults(true);
      }
      performSearch(value);
    } else {
      setOpenSearchResults(false);
      setSearchResults([]);
    }
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue('');
    setSearchResults([]);
    setOpenSearchResults(false);
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'category':
        return <CategoryIcon />;
      case 'subcategory':
        return <SubdirectoryArrowRightIcon />;
      case 'audio':
        return <AudiotrackIcon />;
      case 'artist':
        return <PersonIcon />;
      case 'review':
        return <ReviewsIcon />;
      default:
        return <FindInPageTwoToneIcon />;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.route);
    handleClose();
  };

  return (
    <>
      <Tooltip arrow title="Search">
        <IconButton color="primary" onClick={handleClickOpen}>
          <SearchTwoToneIcon />
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
        scroll="paper"
        onClose={handleClose}
      >
        <DialogTitleWrapper>
          <SearchInputWrapper
            value={searchValue}
            autoFocus={true}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon />
                </InputAdornment>
              )
            }}
            placeholder="Search terms here..."
            fullWidth
            label="Search"
          />
        </DialogTitleWrapper>
        <Divider />

        {openSearchResults && (
          <DialogContent>
            <Box
              sx={{ pt: 0, pb: 1 }}
              display="flex"
              justifyContent="space-between"
            >
              <Typography variant="body2" component="span">
                Search results for{' '}
                <Typography
                  sx={{ fontWeight: 'bold' }}
                  variant="body1"
                  component="span"
                >
                  {searchValue}
                </Typography>
              </Typography>
              <Link href="#" variant="body2" underline="hover">
                Advanced search
              </Link>
            </Box>
            <Divider sx={{ my: 1 }} />
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2">Searching...</Typography>
              </Box>
            ) : searchResults.length > 0 ? (
              <List disablePadding>
                {searchResults.map((result, index) => (
                  <div key={result.id}>
                    <ListItem button onClick={() => handleResultClick(result)}>
                      <Hidden smDown>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              background: (theme: Theme) =>
                                theme.palette.secondary.main
                            }}
                          >
                            {getResultIcon(result.type)}
                          </Avatar>
                        </ListItemAvatar>
                      </Hidden>
                      <Box flex="1">
                        <Box display="flex" justifyContent="space-between">
                          <Typography
                            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                            variant="body2"
                          >
                            {result.name} ({result.type})
                          </Typography>
                        </Box>
                        {result.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: (theme: Theme) =>
                                lighten(theme.palette.secondary.main, 0.5)
                            }}
                          >
                            {result.description.length > 100
                              ? `${result.description.substring(0, 100)}...`
                              : result.description}
                          </Typography>
                        )}
                      </Box>
                      <ChevronRightTwoToneIcon />
                    </ListItem>
                    {index < searchResults.length - 1 && (
                      <Divider sx={{ my: 1 }} component="li" />
                    )}
                  </div>
                ))}
              </List>
            ) : searchValue.trim() && !loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2">No results found for "{searchValue}"</Typography>
              </Box>
            ) : null}
            {searchResults.length > 0 && (
              <>
                <Divider sx={{ mt: 1, mb: 2 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
        )}
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;
